// src/pages/CaseHistoryPage/fields/MilestoneGrid.js
import React from 'react';

/**
 * §5 developmental milestones: age reached, in months, per row.
 * "In range?" is computed against the typical age carried in the schema, so a
 * clinician can correct those reference ages without a code change.
 */
export default function MilestoneGrid({ question, value = {}, onChange, onBlur, lang, disabled }) {
    const rows = question.rows || [];
    const set = (rowKey, raw) => {
        const next = { ...value };
        if (raw === '') delete next[rowKey];
        else next[rowKey] = Number(raw);
        onChange(next);
    };

    const outOfRange = (row) => {
        const months = value[row.key];
        return typeof months === 'number' && typeof row.typical_months === 'number'
            && months > row.typical_months;
    };

    /** Far earlier than any child reaches it — almost always years written in a
     * months column. "Toilet trained at 1" read as one month, and the row answered
     * "हाँ". Half the typical age is the threshold, with a floor of two months so a
     * genuinely early child is not questioned: head control at 2 stays "हाँ".
     *
     * It only asks. An intake worker has to be able to record what the family said,
     * including something surprising. */
    const implausiblyEarly = (row) => {
        const months = value[row.key];
        if (typeof months !== 'number' || typeof row.typical_months !== 'number') return false;
        return months < Math.max(2, row.typical_months / 2);
    };

    const late = rows.filter(outOfRange);
    const early = rows.filter(implausiblyEarly);
    const lateKeys = late.map((r) => r.key);
    const domains = question.domains || {};
    const motorLate = (domains.motor || []).some((k) => lateKeys.includes(k));
    const speechLate = (domains.speech || []).some((k) => lateKeys.includes(k));

    return (
        <div className="ch-grid-wrap">
            <table className="ch-grid">
                <thead>
                    <tr>
                        <th>{lang === 'en' ? 'Milestone' : 'पड़ाव'}</th>
                        {lang === 'both' && <th>Milestone</th>}
                        <th>{lang === 'en' ? 'Months' : 'महीने'}</th>
                        <th>{lang === 'en' ? 'Typical by' : 'आमतौर पर'}</th>
                        <th>{lang === 'en' ? 'In range?' : 'ठीक है?'}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key} className={outOfRange(row) ? 'late' : implausiblyEarly(row) ? 'early' : ''}>
                            <td className={lang === 'en' ? '' : 'hi'}>{lang === 'en' ? row.en : row.hi}</td>
                            {lang === 'both' && <td>{row.en}</td>}
                            <td>
                                <input
                                    type="number" min="0" max="240" inputMode="numeric"
                                    value={value[row.key] ?? ''}
                                    onChange={(e) => set(row.key, e.target.value)}
                                    onBlur={onBlur}
                                    disabled={disabled}
                                    aria-label={row.en}
                                />
                            </td>
                            <td className="muted">{row.typical_months}</td>
                            <td>
                                {value[row.key] === undefined ? <span className="ch-chip">—</span>
                                    : outOfRange(row) ? <span className="ch-chip bad">{lang === 'en' ? 'Late' : 'देर'}</span>
                                        : implausiblyEarly(row) ? <span className="ch-chip warn">{lang === 'en' ? 'Check' : 'जाँचें'}</span>
                                            : <span className="ch-chip ok">{lang === 'en' ? 'Yes' : 'हाँ'}</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {early.length > 0 && (
                <div className="ch-note amber">
                    <span className="hi">क्या यह महीनों में लिखा है? 3 साल = 36 महीने।</span>
                    {' '}Is this in months? 3 years = 36. {early.length} row(s) look too early for
                    the milestone — check with the family, and leave the value as they said it.
                </div>
            )}
            {late.length >= 4 && (
                <div className="ch-note amber">
                    {late.length} milestones are later than typical. Worth flagging to the supervising therapist.
                </div>
            )}
            {motorLate && speechLate && (
                <div className="ch-note blue">
                    Both movement and speech milestones are late. That pattern points to a broader
                    developmental question rather than speech alone — speech-only delay looks different.
                </div>
            )}
        </div>
    );
}
